import { useTranslation } from '@multaqa/i18n'
import { useQueries } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useCostCenters } from '@/features/accounting-cost-centers/api'
import { fetchCustomer } from '@/features/customers/api'
import { useCustomers } from '@/features/customers/hooks/use-customers'
import { fetchSupplier } from '@/features/suppliers/api'
import { useSuppliers } from '@/features/suppliers/hooks/use-suppliers'
import { useAnalysisTags } from '@/lib/accounting-analysis-tags'
import { useJournals } from '@/lib/accounting-journals'
import { localizedText } from '@/lib/me'

type RecordOption = { value: string; label: string }

export function useConditionRecordOptions(
  source: string | undefined,
  search: string,
  selectedIds: string[],
  companyId: string | null,
  locale: string,
) {
  const { t } = useTranslation('condition-filter')
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim())
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => window.clearTimeout(timeout)
  }, [search])

  const customers = useCustomers(
    {
      search: source === 'customer' || source === 'partner' ? debouncedSearch : '',
      page: 1,
      per_page: 50,
    },
    source === 'customer' || source === 'partner',
  )
  const suppliers = useSuppliers(
    {
      search: source === 'supplier' || source === 'partner' ? debouncedSearch : '',
      page: 1,
      per_page: 50,
    },
    source === 'supplier' || source === 'partner',
  )
  const journals = useJournals(source === 'journal' ? companyId : null)
  const tags = useAnalysisTags(source === 'analysis_tag' ? companyId : null)
  const costCenters = useCostCenters({ enabled: source === 'cost_center' })
  const customerIds = source === 'customer' || source === 'partner' ? selectedIds : []
  const supplierIds = source === 'supplier' || source === 'partner' ? selectedIds : []
  const details = useQueries({
    queries: [
      ...customerIds.map((id) => ({
        queryKey: ['condition-filter', 'customer', id],
        queryFn: () => fetchCustomer(id),
      })),
      ...supplierIds.map((id) => ({
        queryKey: ['condition-filter', 'supplier', id],
        queryFn: () => fetchSupplier(id),
      })),
    ],
  })

  const customerRows = customers.data?.data ?? []
  const supplierRows = suppliers.data?.data ?? []
  const options: RecordOption[] = []
  if (source === 'customer' || source === 'partner') {
    options.push(
      ...customerRows.map((row) => ({
        value: row.id,
        label:
          source === 'partner'
            ? t('recordKind.customer', { name: localizedText(row.name, locale) })
            : localizedText(row.name, locale),
      })),
    )
  }
  if (source === 'supplier' || source === 'partner') {
    options.push(
      ...supplierRows.map((row) => ({
        value: row.id,
        label:
          source === 'partner'
            ? t('recordKind.supplier', { name: localizedText(row.name, locale) })
            : localizedText(row.name, locale),
      })),
    )
  }
  if (source === 'journal')
    options.push(
      ...(journals.data ?? []).map((row) => ({
        value: row.id,
        label: localizedText(row.name, locale),
      })),
    )
  if (source === 'analysis_tag')
    options.push(
      ...(tags.data ?? []).map((row) => ({
        value: row.id,
        label: localizedText(row.name, locale),
      })),
    )
  if (source === 'cost_center')
    options.push(
      ...(costCenters.data ?? []).map((row) => ({
        value: row.id,
        label: localizedText(row.name, locale),
      })),
    )
  for (const [index, result] of details.entries()) {
    const row = result.data as { id?: string; name?: Record<string, unknown> } | undefined
    if (row?.id && row.name) {
      const kind =
        source === 'partner' ? (index < customerIds.length ? 'customer' : 'supplier') : undefined
      options.push({
        value: row.id,
        label: kind
          ? t(`recordKind.${kind}`, { name: localizedText(row.name, locale) })
          : localizedText(row.name, locale),
      })
    }
  }
  return {
    options: [...new Map(options.map((option) => [option.value, option])).values()],
    isLoading:
      customers.isLoading ||
      suppliers.isLoading ||
      journals.isLoading ||
      tags.isLoading ||
      costCenters.isLoading,
  }
}
